import {
    CoreButtonView,
    CoreImageView,
    CoreLayoutView,
    CoreTextView,
    ORIENTATION_ENUM,
    type TCoreButtonStyleProps,
    type TCoreButtonViewProps,
    type TCoreLayoutStyleProps,
} from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { AssetsSvgMap, THandleClick, TWidgetCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { ElementType } from 'react';
import { SemanticColor, semanticColors } from '../../../theme/colors';
import { TConsumerItem } from '../../../types/data';
import { TransactionStatusEnum } from '../../../types/enums';
import { TextStyle } from '../../../types/styles';
import { getHref } from '../../Image/imageUtils';
import { VisualView } from '../../Image/VisualView';
import { parseMetaText } from './metaTextUtils';
import styles from './styles/rowTransaction.module.css';

type TRowTransaction = {
    as: ElementType;
    handleClick?: THandleClick;
    item: TConsumerItem;
    parentStyleNames?: string[];
} & Pick<TWidgetCommonOption, 'parentStyleNames'>;

const ellipsis = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
} as React.CSSProperties;

const CONTAINER_STYLE_PROPS: TCoreButtonStyleProps = {
    main: semanticColors[SemanticColor.FillSecondary],
    onMain: semanticColors[SemanticColor.TextPrimary],
    fillContainer: true,
    borderRadius: 0,
    gap: 0,
    height: 72,
    padding: { top: 16, right: 24, bottom: 16, left: 24 },
    textAlign: 'start',
    cursorDisabled: 'default',
    hovered: {
        main: semanticColors[SemanticColor.FillSecondaryHovered],
    },
    pressed: {
        main: semanticColors[SemanticColor.FillSecondaryPressed],
    },
};

const BADGE_CONTAINER_STYLE_PROPS: TCoreLayoutStyleProps = {
    orientation: ORIENTATION_ENUM.BOX,
    backgroundColor: semanticColors[SemanticColor.FillPrimary],
    borderRadius: 24,
    padding: { top: 5, right: 10, bottom: 5, left: 10 },
    width: 'max-content',
};

export function RowTransaction({ item, as, handleClick, parentStyleNames = [] }: TRowTransaction) {
    const isDisabled = item?.enabled === false || item?.clickable === false;
    const title = item?.title;
    const subtitle = [item?.subtitle, item?.additionalSubtitle].join(' ');
    const currency = item?.currency;
    const direction = item?.direction;
    const iconName = item?.iconName;
    const { amount: metaAmount, currency: metaCurrency } = parseMetaText(item?.metaText);
    const avatar = item?.avatar;
    const flagIconSmall = item?.flagIconSmall;
    const statusFailed = item?.status ? item?.status === TransactionStatusEnum.FAIL : false;
    const isExchange = direction === 'EXCHANGE';

    const getStatusIcon = (item: TConsumerItem) => {
        if (!item?.status) return;
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
                return 'ic_check_circle_small';
            case TransactionStatusEnum.FAIL:
                return 'ic_x_circle_small';
            default:
                return 'ic_clock_filled_small';
        }
    };

    const getStatusIconTestSuffix = (item: TConsumerItem) => {
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
                return 'Success';
            case TransactionStatusEnum.FAIL:
                return 'Fail';
            default:
                return 'Waiting';
        }
    };

    const getIconColor = (item: TConsumerItem): SemanticColor => {
        if (!item?.status) return SemanticColor.IconPrimary;
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.FAIL:
                return SemanticColor.IconPrimaryDisabled;
            case TransactionStatusEnum.SUCCESS:
                return SemanticColor.IconEmphasised;
            default:
                return SemanticColor.IconSecondary;
        }
    };

    const getTextColor = (item: TConsumerItem): SemanticColor => {
        if (!item?.status) return SemanticColor.TextPrimary;
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
            case TransactionStatusEnum.SUCCESSFUL_TOPUP:
                return item.direction === 'INCOME' ? SemanticColor.TextEmphasised : SemanticColor.TextPrimary;
            case TransactionStatusEnum.FAIL:
                return SemanticColor.TextPrimaryDisabled;
            default:
                return SemanticColor.TextSecondary;
        }
    };

    const iconStatus = AssetsSvgMap[getStatusIcon(item) as keyof typeof AssetsSvgMap] ?? AssetsSvgMap['ic_clock_filled_small'];
    const amountPrefix = direction === 'INCOME' ? '+' : '-';

    const isShowInfoIcon = item?.status !== TransactionStatusEnum.NONE && item?.status !== TransactionStatusEnum.SUCCESSFUL_TOPUP;

    const uid = 'rowTransaction_' + item.id;
    const widget = { uid, data: {}, options: {} } as TCoreButtonViewProps['widget'];

    return (
        <CoreButtonView
            widget={widget}
            as={as}
            isDisabled={isDisabled}
            styleProps={CONTAINER_STYLE_PROPS}
            parentStyleNames={parentStyleNames}
            styleName="transactionRow"
            onClick={() => handleClick?.(item.id, item)}
        >
            {iconName && (
                <VisualView
                    uid={`transactionRowIcon_${item.id}`}
                    iconName={iconName}
                    rightMargin
                    iconHierarchy={'secondary'}
                    iconVariant={'circle'}
                    flagIconSmall={flagIconSmall}
                    size={'small'}
                    isDisabled={statusFailed}
                    parentStyleNames={parentStyleNames}
                    styleName="transactionRowIcon"
                />
            )}
            {avatar && (
                <VisualView
                    uid={`transactionRowIcon_${item.id}`}
                    avatar={avatar}
                    rightMargin
                    flagIconSmall={flagIconSmall}
                    size={'small'}
                    isDisabled={statusFailed}
                    parentStyleNames={parentStyleNames}
                    styleName="transactionRowIcon"
                />
            )}
            <div className={styles.transactionContent}>
                <div className={styles.transactionRow}>
                    <CoreTextView
                        uid={`transactionRowTitle_${item.id}`}
                        text={title}
                        as="span"
                        parentStyleNames={parentStyleNames}
                        styleName="transactionRowTitle"
                        styleProps={{
                            ...TextStyle.main,
                            main: semanticColors[statusFailed ? SemanticColor.TextPrimaryDisabled : SemanticColor.TextPrimary],
                        }}
                        style={ellipsis}
                    />
                </div>
                {subtitle && (
                    <div className={styles.transactionRow}>
                        <CoreTextView
                            uid={`transactionRowSubtitle_${item.id}`}
                            text={subtitle}
                            as="span"
                            parentStyleNames={parentStyleNames}
                            styleName="transactionRowSubtitle"
                            styleProps={{
                                ...TextStyle.secondary,
                                main: semanticColors[statusFailed ? SemanticColor.TextPrimaryDisabled : SemanticColor.TextSecondary],
                            }}
                            style={ellipsis}
                        />
                    </div>
                )}
            </div>
            <div className={styles.transactionRight}>
                {item?.amount && (
                    <div className={clsx(styles.transactionRow, styles.alignRight)}>
                        <CoreTextView
                            uid={`transactionRowAmount_${item.id}`}
                            text={`${amountPrefix} ${item.amount} ${currency?.toLowerCase()}`}
                            as="span"
                            parentStyleNames={parentStyleNames}
                            styleName="transactionRowAmount"
                            textTransformation={[
                                {
                                    text: item.amount + '',
                                    useLocaleNumberFormat: true,
                                },
                                {
                                    text: currency?.toLowerCase(),
                                    smallCaps: true,
                                },
                            ]}
                            styleProps={{
                                ...TextStyle.main,
                                main: semanticColors[getTextColor(item)],
                            }}
                        />
                    </div>
                )}
                <ConditionalRender condition={isShowInfoIcon}>
                    <div className={clsx(styles.transactionRow, styles.alignRight)}>
                        <ConditionalRender condition={!item?.metaText && !!item?.status}>
                            <CoreImageView
                                widget={
                                    {
                                        uid: `transactionRowStatusIcon${getStatusIconTestSuffix(item)}_${item.id}`,
                                        data: {},
                                        options: {},
                                    } as TCoreButtonViewProps['widget']
                                }
                                src={getHref(iconStatus)}
                                defaultIcon={getHref('ic_clock_filled_small')}
                                parentStyleNames={parentStyleNames}
                                styleName="rowTransactionStatusIcon"
                                styleProps={{
                                    width: 16,
                                    height: 16,
                                    iconColor: semanticColors[getIconColor(item)],
                                }}
                            />
                        </ConditionalRender>
                        <ConditionalRender condition={!!item?.metaText && !isExchange}>
                            <CoreLayoutView
                                uid={`transactionRowBadge_${item.id}`}
                                parentStyleNames={parentStyleNames}
                                styleName="transactionRowBadge"
                                styleProps={BADGE_CONTAINER_STYLE_PROPS}
                            >
                                <CoreTextView
                                    uid={`transactionRowBadgeText_${item.id}`}
                                    text={item?.metaText}
                                    as="span"
                                    parentStyleNames={parentStyleNames}
                                    styleName="transactionRowBadgeText"
                                    styleProps={{
                                        ...TextStyle.tag,
                                        main: semanticColors[SemanticColor.TextPrimaryInverted],
                                        textTransform: 'uppercase',
                                    }}
                                />
                            </CoreLayoutView>
                        </ConditionalRender>
                        <ConditionalRender condition={!!item?.metaText && isExchange}>
                            <CoreTextView
                                uid={`transactionRowBadgeText_${item.id}`}
                                text={item?.metaText}
                                as="span"
                                parentStyleNames={parentStyleNames}
                                styleName="transactionRowBadgeText"
                                textTransformation={[
                                    {
                                        text: metaAmount,
                                        useLocaleNumberFormat: true,
                                    },
                                    {
                                        text: metaCurrency,
                                        smallCaps: true,
                                    },
                                ]}
                                styleProps={{
                                    ...TextStyle.main,
                                    main: semanticColors[SemanticColor.TextEmphasised],
                                }}
                            />
                        </ConditionalRender>
                    </div>
                </ConditionalRender>

                <ConditionalRender condition={!isShowInfoIcon && !!subtitle}>
                    <div style={{ width: '16px', height: '16px' }} />
                </ConditionalRender>
            </div>
        </CoreButtonView>
    );
}
